import {
    AssertionDescriptorDisplayCategoryEnum,
    CaseDisplayCategoryEnum,
    UserRightsEnum,
} from '../../utilities/Constants';

import { Accordion } from 'react-bootstrap';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';
import { AccordionWrapper } from './AccordionWrapper';
import Authorization from '../../stores/Authorization';
import { CaseFileSection } from './CaseFileSection';
import { IDisplaySectionModel } from '../../interfaces/IDisplaySectionModel';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import classNames from 'classnames';
import { useCaseFileContext } from '../../contexts/useCaseFileContext';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useState } from 'react';
import { useZoneContext } from '../../contexts/useZoneContext';

const getSectionIsTopLevel = (section: IDisplaySectionModel) => {
    return section.level === 1;
};

export interface ICaseFileSectionsProps {
    sections: IDisplaySectionModel[];
    user: IUserModel;
    validation: IValidation;
}

const getChildAccordionKeys = (sections: IDisplaySectionModel[], parentPath = ''): string[] => {
    return sections?.flatMap((section) => {
        const currentKey = parentPath ? `${parentPath}_${section.id}` : section.id.toString();
        const nestedKeys = section.sections
            ? getChildAccordionKeys(section.sections, currentKey)
            : [];
        return [currentKey, ...nestedKeys];
    });
};

export function CaseFileSections({ sections, user, validation }: ICaseFileSectionsProps) {
    const queryParams = useQueryParams();
    const { currentCase } = useCaseFileContext();
    const { zoneGuid } = useZoneContext();

    const expandCheckmateAssessment = queryParams.get('ca') === 'true';
    const checkmateAssessmentSection = sections.find(
        (section) => section.id === AssertionDescriptorDisplayCategoryEnum.CheckmateAssessment.Value
    );

    const defaultActiveKey =
        expandCheckmateAssessment && checkmateAssessmentSection
            ? [checkmateAssessmentSection.id.toString()]
            : ['-1'];

    const isNewCase = !currentCase.guid;
    const [activeKey, setActiveKey] = useState<AccordionEventKey>(defaultActiveKey);

    const handleAccordionSelectChange = (key: string, section: IDisplaySectionModel) => {
        if (!getSectionIsTopLevel(section)) return;

        const childKeys = getChildAccordionKeys(section.sections, section.id.toString()) ?? [];

        // convoluted logic here because AccordionEventKey can be a single value or an Array
        if (!key) {
            setActiveKey(['-1']);
        } else if (Array.isArray(activeKey)) {
            if (activeKey.some((ak) => ak === key)) {
                setActiveKey((prevKeys) => {
                    if (Array.isArray(prevKeys)) {
                        return prevKeys
                            ?.filter((pk) => pk !== key)
                            .filter((pk) => !pk.startsWith(`${key}_`));
                    }
                    return prevKeys;
                });
            } else {
                setActiveKey([...activeKey, key, ...childKeys]);
            }
        } else {
            setActiveKey(['-1', key, ...childKeys]);
        }
    };

    const getSectionIsOpen = (idx: string) => {
        if (Array.isArray(activeKey)) {
            return activeKey?.some((key) => key === idx);
        } else {
            return activeKey === idx;
        }
    };

    const filteredSections = sections.filter((section) => {
        const isCheckmateAssessmentSection =
            section.id === AssertionDescriptorDisplayCategoryEnum.CheckmateAssessment.Value;

        const showCheckmateAsssessmentSection = Authorization.userHasAtLeastOneRight(
            user,
            [
                UserRightsEnum.AssessmentBestFitCasesGet,
                UserRightsEnum.AssessmentCaseStatisticsGet,
                UserRightsEnum.AssessmentPredictedOutcomeGet,
                UserRightsEnum.AssessmentThreatScoreGet,
            ],
            zoneGuid
        );

        return isCheckmateAssessmentSection ? showCheckmateAsssessmentSection : true;
    });

    const getAccordionClassName = (section: IDisplaySectionModel) => {
        const isCheckmateAssessmentSection =
            section.id === AssertionDescriptorDisplayCategoryEnum.CheckmateAssessment.Value;

        const checkmateAssessmentClassName = isCheckmateAssessmentSection
            ? 'checkmate-assessment'
            : '';

        const topLevelClassName = getSectionIsTopLevel(section) ? 'top-level' : '';

        return classNames(topLevelClassName, checkmateAssessmentClassName);
    };

    return (
        <Accordion activeKey={activeKey} flush alwaysOpen>
            {filteredSections.map((section, idx) => {
                const isFirstSection = idx === 0;
                const isTopLevel = getSectionIsTopLevel(section);

                const nonExpandableSectionsOnCreate = [
                    CaseDisplayCategoryEnum.Deposition.Value,
                    CaseDisplayCategoryEnum.CaseNotes.Value,
                    CaseDisplayCategoryEnum.CaseExpert.Value,
                ].includes(section.id);

                const isExpandable =
                    isFirstSection ||
                    (!isNewCase && isTopLevel) ||
                    (isNewCase && isTopLevel && !nonExpandableSectionsOnCreate);

                const isOpen = isExpandable && getSectionIsOpen(section.id.toString());
                const isLastSection = idx === section.sections?.length - 1;
                const accordionClassName = getAccordionClassName(section);

                return (
                    <AccordionWrapper
                        key={idx}
                        accordionIndex={section.id}
                        className={accordionClassName}
                        isOpen={isOpen}
                        onAccordionSelectChange={handleAccordionSelectChange}
                        shouldWrap={true}
                        section={section}
                        isExpandable={isExpandable}
                        isTopLevel={isTopLevel}
                        isLastSection={isLastSection}
                    >
                        <CaseFileSection
                            displayAsCondensed={true}
                            isOpen={isOpen}
                            key={idx}
                            numberOfColumns={2}
                            parentKey={section.id.toString()}
                            section={section}
                            user={user}
                            validation={validation}
                        />
                    </AccordionWrapper>
                );
            })}
        </Accordion>
    );
}
